import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { usePartnerScore } from "Partner/Api/Query/usePartnerScoreQuery";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { Alert, Box, CircularProgress, Grid2, Typography } from "@mui/material";

type PartnerScoreInfoWidgetProps = {
	partnerId: number;
};

const scoreResource = Resources.base.partner.detail.score;

export function PartnerScoreInfoWidget({
	partnerId,
}: PartnerScoreInfoWidgetProps) {
	const { t } = useResource();
	const { data, isLoading, error } = usePartnerScore(partnerId);

	if (isLoading) {
		return (
			<BaseCard title={t(scoreResource.title)}>
				<Box display="flex" justifyContent="center" p={2}>
					<CircularProgress />
				</Box>
			</BaseCard>
		);
	}

	if (error) {
		return (
			<BaseCard title={t(scoreResource.title)}>
				<Alert severity="error">Failed to load score</Alert>
			</BaseCard>
		);
	}

	if (!data) {
		return null;
	}

	const score = data;

	return (
		<BaseCard title={t(scoreResource.title)}>
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12 }} mt={0.5}>
					<Box display="flex" alignItems="center" gap={1}>
						<Typography variant="h4" color="primary" fontWeight={700}>
							{score.internalScore}
						</Typography>
						<Typography variant="subtitle1" color="textSecondary">
							/ 100
						</Typography>
					</Box>
					<Typography variant="caption" color="textSecondary">
						{t(scoreResource.internalScore)}
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 3 }} mt={0.5}>
					<Typography variant="h6" fontWeight={600}>
						{score.ratingScore}
					</Typography>
					<Typography variant="caption" color="textSecondary" display="block">
						{t(scoreResource.ratingScore)}
					</Typography>
					<Typography variant="caption" color="textSecondary">
						({t(scoreResource.rawRating)}: {score.rawRating})
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 3 }} mt={0.5}>
					<Typography variant="h6" fontWeight={600}>
						{score.acceptanceScore}
					</Typography>
					<Typography variant="caption" color="textSecondary" display="block">
						{t(scoreResource.acceptanceScore)}
					</Typography>
					<Typography variant="caption" color="textSecondary">
						({t(scoreResource.rawAcceptanceRate)}: {score.rawAcceptanceRate})
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 3 }} mt={0.5}>
					<Typography variant="h6" fontWeight={600}>
						{score.claimScore}
					</Typography>
					<Typography variant="caption" color="textSecondary" display="block">
						{t(scoreResource.claimScore)}
					</Typography>
					<Typography variant="caption" color="textSecondary">
						({t(scoreResource.rawClaimRate)}: {score.rawClaimRate})
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: 3 }} mt={0.5}>
					<Typography variant="h6" fontWeight={600}>
						{score.rejectionScore}
					</Typography>
					<Typography variant="caption" color="textSecondary" display="block">
						{t(scoreResource.rejectionScore)}
					</Typography>
					<Typography variant="caption" color="textSecondary">
						({t(scoreResource.rawRejectionRate)}: {score.rawRejectionRate})
					</Typography>
				</Grid2>
			</Grid2>
		</BaseCard>
	);
}
